import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

import * as Bridge from '@/api/BridgeApi'
import { ApiUrlErrorMsg, goPaymentMainUrl } from '@/components/App'
import configs from '@/configs'
import { IServerResponse, STATUS_CODE } from '@/domains/CommonDomain'
import { INativeInfo } from '@/utils/bridgeUtils/features/getNativeInfo'
import { ApiError, newError } from '@/utils/errorUtils'
import { push } from '@/utils/historyUtils'

import { StoreType } from '..'

const CHANNEL = 'MBK'
const BASE_VERSION = 'v1'
const BASE_VERSION_BETA = 'v2'
const TIMEOUT = 60000
export const XSRF_HEADER_NAME = 'x-xsrf-token'
export const USE_XSRF_HEADER = { xsrfHeaderName: XSRF_HEADER_NAME }
export let XSRF_TOKEN: undefined | string = ''

export const LB_AUTH_NAME = 'lbauth'
export let LB_AUTH: undefined | string = undefined

/* Referral */
let _tokens = {
  lbline: '',
  lblineId: '',
}

export const setTokens = (accessToken: string, userId: string) => {
  _tokens.lbline = accessToken
  _tokens.lblineId = userId
}

const getTokens = () => {
  if (_tokens.lbline === '' || _tokens.lblineId === '') {
    return null
  }

  return _tokens
}

export const axiosInstance = axios.create({
  baseURL: configs.BASE_URL,
  timeout: TIMEOUT,
  xsrfHeaderName: '',
})
axiosInstance.defaults.withCredentials = true

export const axiosMockInstance = axios.create({
  baseURL: 'http://192.168.35.205:8080',
  timeout: TIMEOUT,
  xsrfHeaderName: '',
})
const versionize = (url: string, betaFlag = false): string => {
  if (/^\/v\d+\//.test(url)) {
    return url
  }
  // console.log('betaFlag:', betaFlag)

  return `/${betaFlag === false ? BASE_VERSION : BASE_VERSION_BETA}${url}`
}

const toBankAPI = (urlInfo: any) =>
  `${configs.BASE_URL}/BANKING-API` +
  versionize(typeof urlInfo !== 'string' ? urlInfo['url'] : urlInfo, urlInfo['beta'])
const toCustomerAPI = (url: string) => `${configs.BASE_URL}/CUSTOMER-API` + versionize(url)
const toCsrfApi = (url: string) => `${configs.BASE_URL}` + url
const toMock = (url: string) => `http://192.168.35.205:8080/MOCK-API` + versionize(url)
const toCardApi = (url: string) => `https://lb-uat.linebank.co.id:8443` + versionize(url)

const bankApi = {
  post<T>(urlInfo: any, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.post(toBankAPI(urlInfo), data, config)
  },
}
const customerApi = {
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.post(toCustomerAPI(url), data, config)
  },
  postEx<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.post(toCustomerAPI(url), data, config)
  },
}
const csrfApi = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.get(toCsrfApi(url), config)
  },
}
const mockApi = {
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return axiosMockInstance.post(toMock(url), data, config)
  },
}

const fetchcardApi = {
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.post(toCardApi(url), data, config)
  },
}

// Network Off 시
function isNetworkOff() {
  return !window.navigator.onLine
}

// API G/W 장애 시
// Maintenance 설정으로 인한 Preflight 요청 시
function isNetworkError(error: AxiosError) {
  return !!error.isAxiosError && !error.response
}

// Server에서 5XX Status 전달시
function isServerError(error: AxiosError) {
  return !!error.isAxiosError && error.response && error.response.status
}

export let USE_OS_HEADER = ''

// function : restapi 호출시 정상 url 체크
const checkApiCurrentUrl: any = (res: any) => {
  // 정상 url 체크 데이터
  const availableUrl = [
    { url: '/bill-payment?', restUrl: '/inquire', mainUrl: goPaymentMainUrl },
    { url: '/bill-payment/product-group/', restUrl: '/inquire', mainUrl: goPaymentMainUrl },
    { url: '/bill-payment/product/', restUrl: '/inquire', mainUrl: goPaymentMainUrl },
    { url: '/bill-payment/amount/', restUrl: '/inquire', mainUrl: goPaymentMainUrl },
  ]

  const findAvailableUrl: any = () => {
    return availableUrl.filter(row => {
      return window.location.href.indexOf(row.url) > -1
    })
  }

  const findAvailableApi: any = (resApi: any) => {
    return availableUrl.filter(row => {
      return (resApi.config.url ?? '').indexOf(row.restUrl) > -1
    })
  }

  /***************************************************************
   *  return code
   *  'success' : 정상
   *  'fail'    : 비정상(각 기능 main 페이지로)
   *  'none'    : 해당없음
   ***************************************************************/

  const availableUrlObj = findAvailableUrl()
  const availableApiObj = findAvailableApi(res)

  if (availableUrlObj?.length === 0 && availableApiObj?.length === 0) {
    return { code: 'none', url: '' }
  } else if (availableUrlObj?.length > 0 && availableApiObj?.length > 0) {
    return { code: 'success', url: '' }
  } else if (availableUrlObj?.length === 0 && availableApiObj?.length > 0) {
    return { code: 'fail', url: availableApiObj[0].mainUrl }
  }
}

export const init = (store: StoreType, nativeInfo: INativeInfo) => {
  function handleRequest(config: AxiosRequestConfig) {
    // [Workaround]
    //  XSRF_TOKEN cookie의 Secure=true 옵션때문에
    //  axios의 xsrfCookieName 설정을 사용하지 못하여
    //  response header의 X-XSRF-TOKEN 값을 저장하여 사용
    const useXsrfHeader = config.xsrfHeaderName === XSRF_HEADER_NAME
    USE_OS_HEADER = nativeInfo!.osModel
    config.headers = {
      lbapplicationId: nativeInfo!.applicationId,
      lbchannel: CHANNEL,
      lbdeviceId: nativeInfo!.deviceId,
      lblang: nativeInfo!.language,
      lbos: nativeInfo!.osModel,
      lbauth: LB_AUTH ?? nativeInfo!.authToken,
      ...(useXsrfHeader && { [XSRF_HEADER_NAME.toUpperCase()]: XSRF_TOKEN }),
      /* Referral */
      ...getTokens(),
    }
    //document.cookie = `XSRF-TOKEN=${XSRF_TOKEN}`
    config.withCredentials = true
    // console.log('config.headers :', config.headers)

    return config
  }

  function handleMockRequest(config: AxiosRequestConfig) {
    config.headers = {
      withCredentials: true,
      'content-Type': 'application/json; charset=utf8',
    }

    return config
  }

  function handleResponse<T>(response: AxiosResponse<IServerResponse<T>>): T {
    console.log('response :: ', response)
    if (response.headers[XSRF_HEADER_NAME]) {
      XSRF_TOKEN = response.headers[XSRF_HEADER_NAME]
    }

    if (response.headers[LB_AUTH_NAME]) {
      LB_AUTH = response.headers[LB_AUTH_NAME]
    }

    const checkApiCurrentUrlObj = checkApiCurrentUrl(response)
    console.log('checkApiCurrentUrlObj:::::::::::', checkApiCurrentUrlObj)
    if (checkApiCurrentUrlObj?.code === 'fail') {
      // locale : 'common_error_unknown_desc'
      alert(ApiUrlErrorMsg)
      push(goPaymentMainUrl)
      throw new ApiError(response)
    }

    if (response.data && response.data.code !== STATUS_CODE.SUCCESS) {
      throw new ApiError(response)
    }

    // api 호출 후 수정된 lbauth cookie 값을 app에서 가져가기 위해 호출
    Bridge.requestRefreshAuth()

    return response.data.content
  }

  function handleError(error: AxiosError) {
    if (error.code === STATUS_CODE.NETWORK_TIMEOUT_ERROR) {
      throw newError(STATUS_CODE.NETWORK_TIMEOUT_ERROR)
    }

    if (isNetworkOff()) {
      throw newError(STATUS_CODE.NETWORK_OFF)
    }

    if (isNetworkError(error)) {
      throw newError(STATUS_CODE.NETWORK_ERROR)
    }

    if (isServerError(error)) {
      throw newError(STATUS_CODE.UNKNOWN_SERVER_ERROR)
    }

    throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
  }

  axiosInstance.interceptors.request.use(handleRequest)
  axiosInstance.interceptors.response.use(handleResponse, handleError)

  axiosMockInstance.interceptors.request.use(handleMockRequest)
  axiosMockInstance.interceptors.response.use(handleResponse, handleError)
}

export { bankApi, customerApi, csrfApi, mockApi, fetchcardApi }
